import { ChangeEvent, ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
    GridColumnHeaderParams,
    gridTabIndexColumnHeaderSelector,
    useGridSelector,
    useGridRootProps,
    useGridApiContext,
    gridDataRowIdsSelector,
    gridFilteredRowsLookupSelector,
    GridEvents,
} from '@mui/x-data-grid-premium';
import { Checkbox } from '@mui/material';

export const CheckboxSelectorHeader = (props: GridColumnHeaderParams): ReactElement => {
    const [, forceUpdate] = useState(false);
    const { field } = props;
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const tabIndexState = useGridSelector(apiRef, gridTabIndexColumnHeaderSelector);
    const allRowIds = useGridSelector(apiRef, gridDataRowIdsSelector);
    const selectedRows = apiRef.current.getSelectedRows();
    const filteredLookup = useGridSelector(apiRef, gridFilteredRowsLookupSelector);
    const selectableRowIds = useMemo(() => {
        return allRowIds?.filter((id) => {
            // const row = apiRef.current.getRow(id);
            // if (row == null) return false;
            return !rootProps?.isRowSelectable || rootProps?.isRowSelectable(apiRef.current.getRowParams(id));
        });
    }, [allRowIds, rootProps?.isRowSelectable, rootProps?.columns]);
    const filteredRowIds = useMemo(() => {
        return selectableRowIds.filter((id) => filteredLookup[id]);
    }, [selectableRowIds, filteredLookup]);

    const isChecked = useMemo(() => {
        return Boolean(filteredRowIds.length) && filteredRowIds.every((id) => selectedRows.has(id));
    }, [filteredRowIds, selectedRows]);

    const isIndeterminate = useMemo(() => {
        return Boolean(selectedRows.size) && filteredRowIds.some((id) => !selectedRows.has(id));
    }, [filteredRowIds, selectedRows]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            apiRef.current.selectRows(filteredRowIds, true);
        } else {
            apiRef.current.selectRows([...selectedRows.keys()], false);
        }
    };

    const tabIndex = tabIndexState !== null && tabIndexState.field === field ? 0 : -1;

    useLayoutEffect(() => {
        const element = apiRef.current.getColumnHeaderElement(field);
        if (tabIndex === 0 && element) {
            element!.tabIndex = -1;
        }
    }, [tabIndex, apiRef.current, field]);

    const handleSelectionChange = useCallback(() => {
        forceUpdate((p) => !p);
    }, []);

    useEffect(() => {
        return apiRef.current.subscribeEvent('rowSelectionChange' as GridEvents, handleSelectionChange);
    }, [apiRef.current, handleSelectionChange]);

    return (
        <Checkbox
            tabIndex={tabIndex}
            checked={isChecked}
            indeterminate={isIndeterminate}
            onChange={handleChange}
            inputProps={{ 'aria-label': 'controlled' }}
            disableRipple
            data-automation-id="data-grid-selector-header"
        />
    );
};
